import clsx from 'clsx';
import { format } from 'date-fns';
import { useCallback, useState } from 'react';
import { CalendarProps } from 'react-date-range';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useSiteTimezoneContext } from 'src/contexts/SiteTimezoneContext/SiteTimezoneContext';
import * as selectorStyles from 'src/core/POS/DateRangeSelector/DateRangeSelectorV1.css';
import { vars } from 'src/core/themes';
import { Popover, Stack } from 'src/core/ui';
import { CalendarIcon, CrossIcon, ExpandIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import {
  formatDate,
  tryGetDatePresetSuggestionText,
} from 'src/utils/dateTimeUtils';
import { getLocaleFromLanguageOrCurrent } from 'src/utils/localeUtils';

import * as styles from './DatePicker.css';
import { DatePickerInput } from './DatePickerInput';
import {
  DatePickerPresets,
  DatePickerPresetsProp,
} from './DatePickerPresets/DatePickerPresets';

export type DateSelectorProps = Omit<DatePickerPresetsProp, 'onClick'> &
  Omit<CalendarProps, 'onChange'> & {
    disabled?: boolean;
    defaultDate?: Date;
    placeholderText?: string;
    onChange: (date?: Date, presetName?: string) => void;
    /**
     * If true, the site's timezone will be applied to the date being displayed.
     * the underlying date value (value, data, onDateChange) will not be changed.
     */
    useSiteTimeZone?: boolean;
    /**
     * If true and useSiteTimeZone is false, the trigger displays the date's
     * local calendar value as-is (for values stored without timezone info).
     * Defaults to false, which preserves the legacy rendering.
     */
    useLocalDateDisplay?: boolean;
  } & Omit<CalendarProps, 'onChange'>;

export function DateSelector({
  date,
  defaultDate,
  onChange,
  placeholderText,
  disabled,
  className,
  dateDisplayFormat = 'MM/dd/yyyy',
  color = vars.color.backgroundHighlight,
  locale = getLocaleFromLanguageOrCurrent(),
  useSiteTimeZone = true,
  useLocalDateDisplay = false,
  ...presetProps
}: DateSelectorProps) {
  const { toZonedTime, timeZone } = useSiteTimezoneContext();
  const [isOpen, setIsOpen] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);

  const placeholderTextContent = useContent(placeholderText);

  const defaultDateDisplayText = useContent(ContentId.DateRangeDisplayText);

  const onDatePickerChange = useCallback(
    (date?: Date, presetName?: string) => {
      if (!date) {
        onChange(undefined);
        return;
      }
      onChange(date, presetName);
    },
    [onChange]
  );

  const onClear = useCallback(() => {
    onDatePickerChange(defaultDate ? new Date(defaultDate) : undefined);
  }, [defaultDate, onDatePickerChange]);

  const onSuggestionClick = (date: Date, presetName?: string) => {
    onDatePickerChange(date, presetName);
    setIsOpen(false);
  };

  const getDisplayText = useCallback(() => {
    const suggestionText = tryGetDatePresetSuggestionText(
      date,
      presetProps.targetDate,
      presetProps.inHandDaysBeforeEvent,
      useSiteTimeZone ? toZonedTime : undefined
    );

    if (suggestionText) {
      return suggestionText;
    }

    if (date) {
      if (useSiteTimeZone) {
        return `${formatDate(toZonedTime(date).toISOString(), undefined, timeZone)}`;
      }
      if (useLocalDateDisplay) {
        return format(date, dateDisplayFormat);
      }
      return `${formatDate(date.toISOString(), undefined, timeZone)}`;
    }

    return placeholderText ? placeholderTextContent : defaultDateDisplayText;
  }, [
    date,
    presetProps.targetDate,
    presetProps.inHandDaysBeforeEvent,
    placeholderText,
    placeholderTextContent,
    defaultDateDisplayText,
    useSiteTimeZone,
    useLocalDateDisplay,
    toZonedTime,
    timeZone,
    dateDisplayFormat,
  ]);

  const highlightDropBox = !(
    (date ?? undefined) === (defaultDate ?? undefined)
  );

  return (
    <Popover.Root
      open={isOpen}
      onOpenChange={(isOpen: boolean) => {
        setIsOpen(isOpen);
        if (!isOpen) {
          setShowDatePicker(false);
        }
      }}
    >
      <Popover.Trigger asChild>
        <button
          className={clsx(selectorStyles.dateRangeTrigger, {
            [selectorStyles.dateRangeTriggerActive]: highlightDropBox,
          })}
          disabled={disabled}
        >
          <div className={selectorStyles.dateRangeTriggerContent}>
            <div
              className={
                !date ? styles.noValueSelectedColor : styles.selectedValueColor
              }
            >
              {getDisplayText()}
            </div>
            <div className={selectorStyles.dateRangeTriggerContentRight}>
              {!disabled && date && highlightDropBox && (
                <div className={selectorStyles.iconContainer}>
                  <CrossIcon size={vars.iconSize.xs} onClick={onClear} />
                </div>
              )}
              <ExpandIcon size={vars.iconSize.xs} />
            </div>
          </div>
        </button>
      </Popover.Trigger>
      <Popover.Content align="start" className={styles.datePopoverContent}>
        <div className={selectorStyles.dateRangePickerContainer}>
          <DatePickerPresets
            {...presetProps}
            disabled={disabled}
            onClick={onSuggestionClick}
            useSiteTimeZone={useSiteTimeZone}
          />
          <Stack direction="column" gap="l">
            <button
              className={clsx(selectorStyles.customDateRangeButton)}
              disabled={disabled}
              onClick={() => setShowDatePicker((val) => !val)}
            >
              <div className={selectorStyles.optionText}>
                <Content id={ContentId.CustomDate} />
              </div>
              <CalendarIcon size={vars.iconSize.m} withHoverEffect />
            </button>
            {showDatePicker && (
              <DatePickerInput
                {...presetProps}
                date={date}
                onDateChange={onDatePickerChange}
                className={clsx(className, styles.datePicker)}
                dateDisplayFormat={dateDisplayFormat}
                color={color}
                locale={locale}
                useSiteTimeZone={useSiteTimeZone}
              />
            )}
          </Stack>
        </div>
      </Popover.Content>
    </Popover.Root>
  );
}
